import { jsx as _jsx } from "react/jsx-runtime";
// Internal React context wiring for the adapter seam.
//
// `<PlatformProvider>` is public API; `usePlatformAdapter()` is
// internal — components inside the package call it, consumers never
// do (they only supply the adapter).
import { createContext, useContext } from 'react';
const PlatformContext = createContext(null);
/**
 * Supplies the `PlatformDataAdapter` to every component in the
 * subtree. Mount ONE provider above any `@sb-ai-platform/ui-react`
 * component.
 */
export function PlatformProvider(props) {
    return (_jsx(PlatformContext.Provider, { value: props.adapter, children: props.children }));
}
/**
 * Internal hook — resolves the adapter from context. Throws a clear,
 * actionable error when no provider is mounted (the silent-null
 * alternative produces undebuggable downstream fetch failures).
 */
export function usePlatformAdapter() {
    const adapter = useContext(PlatformContext);
    if (adapter === null) {
        throw new Error('@sb-ai-platform/ui-react: no PlatformDataAdapter found. ' +
            'Wrap your tree in <PlatformProvider adapter={...}> before rendering ui-react components.');
    }
    return adapter;
}
/**
 * Named error for a missing optional adapter method. End-user
 * components fail FAST with an actionable message naming both the
 * method and the component that needs it — the silent alternative is
 * an undebuggable `undefined is not a function` mid-interaction.
 */
export class AdapterCapabilityError extends Error {
    method;
    constructor(method, requiredBy) {
        super(`@sb-ai-platform/ui-react: this adapter does not implement ${method} — required by <${requiredBy}>. Supply an adapter with ${method} to <PlatformProvider> before rendering this component.`);
        this.name = 'AdapterCapabilityError';
        this.method = method;
    }
}
/**
 * Internal hook — resolves the adapter AND asserts the optional
 * end-user methods a component needs are actually present, narrowing
 * the type so call sites don't re-check. Throws
 * `AdapterCapabilityError` (per missing method, first wins) when the
 * consumer's adapter predates the fn-48 additions.
 */
export function useEndUserAdapter(required, requiredBy) {
    const adapter = usePlatformAdapter();
    for (const method of required) {
        if (typeof adapter[method] !== 'function') {
            throw new AdapterCapabilityError(method, requiredBy);
        }
    }
    return adapter;
}
//# sourceMappingURL=context.js.map